<!-- SectionNavigator.vue -->
<template>
  <div class="section-navigator">
    <div class="section-header">
      <div class="section-title-area">
        <h3 class="section-title">Episode Sections</h3>
        <span class="section-count">({{ sections.length }})</span>
      </div>
      <div class="section-controls">
        <button
          v-if="isExpanded"
          class="view-btn"
          :class="{ active: viewMode === 'grouped' }"
          @click="viewMode = 'grouped'"
          title="Group by section type"
        >
          <span>📑</span>
        </button>
        <button
          v-if="isExpanded"
          class="view-btn"
          :class="{ active: viewMode === 'timeline' }"
          @click="viewMode = 'timeline'"
          title="Show as timeline"
        >
          <span>⏱️</span>
        </button>
        <button
          v-if="isExpanded"
          class="view-btn"
          :class="{ active: expandedView }"
          @click="expandedView = !expandedView"
          title="Toggle compact/expanded view"
        >
          <span>{{ expandedView ? '↑' : '↓' }}</span>
        </button>
        <button
          class="toggle-btn"
          @click="isExpanded = !isExpanded"
          title="Toggle section navigator"
        >
          {{ isExpanded ? '▲' : '▼' }}
        </button>
      </div>
    </div>

    <div v-if="isExpanded" class="section-content">
      <!-- No sections message -->
      <div v-if="sections.length === 0" class="no-sections">
        No sections found in this episode
      </div>

      <!-- Grouped View -->
      <div v-else-if="viewMode === 'grouped'" class="section-list grouped-view">
        <div v-for="(group, groupName) in groupedSections" :key="groupName" class="section-group">
          <!-- Group Header -->
          <div
            class="group-header"
            :class="[getGroupClass(groupName)]"
            @click="toggleGroup(groupName)"
          >
            <span class="group-icon">{{ getGroupIcon(groupName) }}</span>
            <span class="group-name">{{ getGroupLabel(groupName) }}</span>
            <span class="group-count">{{ group.length }}</span>
            <span class="group-toggle">{{ collapsedGroups[groupName] ? '▼' : '▲' }}</span>
          </div>

          <!-- Group Items -->
          <div v-if="!collapsedGroups[groupName]" class="group-items">
            <div
              v-for="section in group"
              :key="section.index"
              @click.stop="$emit('section-click', section.index)"
              class="section-item mini"
              :class="{ 'is-active': section.isActive }"
            >
              <span class="segment-number">{{ section.index + 1 }}</span>
              <div v-if="expandedView" class="segment-speaker">{{ section.speaker }}</div>
              <div v-if="section.isActive" class="mini-playing-indicator">▶</div>

              <!-- Marker tooltip icon (if any markers found) -->
              <span
                v-if="section.markers && section.markers.length > 0"
                class="marker-badge"
                :title="formatMarkerTooltip(section.markers)"
              >
                🏷️
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Timeline View -->
      <div v-else class="section-list timeline-view">
        <div
          v-for="(section, idx) in sections"
          :key="idx"
          @click="$emit('section-click', section.index)"
          class="section-item"
          :class="[
            section.colorClass,
            { 'is-active': section.isActive, 'is-expanded': expandedView }
          ]"
        >
          <!-- Compact view for timeline -->
          <div class="section-item-header">
            <span class="section-icon">{{ section.icon }}</span>
            <div class="section-details">
              <div class="section-label">
                <span class="segment-number">#{{ section.index + 1 }}</span>
                {{ section.label }}

                <!-- Marker badge with tooltip (if any markers found) -->
                <span
                  v-if="section.markers && section.markers.length > 0"
                  class="marker-tooltip-wrapper"
                >
                  <span class="marker-badge">🏷️</span>
                  <span class="marker-tooltip">{{ formatMarkerTooltip(section.markers) }}</span>
                </span>
              </div>
              <div v-if="expandedView" class="section-speaker">
                {{ section.speaker }}
              </div>
            </div>
            <div v-if="section.isActive" class="playing-indicator">
              <span>▶</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Scroll to active button when many segments -->
      <div v-if="sections.length > 10" class="scroll-actions">
        <button
          class="scroll-to-active-btn"
          @click="scrollToActive"
          title="Scroll to current segment"
        >
          <span>🔍 Current</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SectionNavigator',
  props: {
    lastConversationUpdate: {
      type: Number,
      default: 0
    },
    parsedConversation: {
      type: Array,
      default: () => []
    },
    activeSegment: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      isExpanded: false,  // Controls whether the entire component content is shown
      expandedView: false,  // Controls compact/expanded view within the component
      viewMode: 'grouped', // 'grouped' or 'timeline'
      sections: [],
      collapsedGroups: {},
      sectionTypes: {
        preview: { icon: '👁️', colorClass: 'preview-section', label: 'Preview' },
        intro: { icon: '🎙️', colorClass: 'intro-section', label: 'Intro' },
        main: { icon: '📝', colorClass: 'main-section', label: 'Main Content' },
        break: { icon: '💰', colorClass: 'break-section', label: 'Sponsor Break' },
        last: { icon: '🏁', colorClass: 'last-section', label: 'Closing' },
        suspense: { icon: '😰', colorClass: 'suspense-section', label: 'Suspense' },
        cliffhanger: { icon: '🧗', colorClass: 'cliffhanger-section', label: 'Cliffhanger' },
        revelation: { icon: '💡', colorClass: 'revelation-section', label: 'Revelation' },
        tension: { icon: '⚡', colorClass: 'tension-section', label: 'Tension' },
        analogy: { icon: '🔄', colorClass: 'analogy-section', label: 'Analogy' },
        humor: { icon: '😂', colorClass: 'humor-section', label: 'Humor' },
        discussion: { icon: '💬', colorClass: 'discussion-section', label: 'Discussion' },
        insight: { icon: '💡', colorClass: 'insight-section', label: 'Insight' },
        resonance: { icon: '🔄', colorClass: 'resonance-section', label: 'Resonance' },
        evolution: { icon: '🌱', colorClass: 'evolution-section', label: 'Evolution' },
        portal: { icon: '🚪', colorClass: 'portal-section', label: 'Portal' },
        custom: { icon: '❓', colorClass: 'custom-section', label: 'Custom' }
      }
    }
  },
  computed: {
    groupedSections() {
      // Group sections by type
      const grouped = {};
      this.sections.forEach(section => {
        if (!grouped[section.type]) {
          grouped[section.type] = [];
        }
        grouped[section.type].push(section);
      });
      return grouped;
    }
  },
  watch: {
    lastConversationUpdate: {
      immediate: true,
      handler: 'extractSections'
    },
    parsedConversation: {
      immediate: true,
      handler: 'extractSections'
    },
    activeSegment: {
      immediate: true,
      handler: 'updateActiveSections'
    },
    sections: {
      immediate: true,
      handler: 'initializeCollapsedState'
    }
  },
  methods: {
    extractSections() {
      // Find all segments in the conversation and extract markers
      const extractedSections = [];
      this.parsedConversation.forEach((segment, index) => {
        // Extract markers from content (anything in square brackets)
        const markers = this.extractMarkers(segment.content);

        // Determine the section type
        let sectionType = segment.section || 'custom';

        // If the section type isn't in our sectionTypes, default to 'custom'
        if (!this.sectionTypes[sectionType]) {
          sectionType = 'custom';
        }

        extractedSections.push({
          type: sectionType,
          index,
          ...this.sectionTypes[sectionType],
          speaker: segment.speaker,
          role: segment.role,
          isActive: index === this.activeSegment,
          markers: markers
        });
      });

      this.sections = extractedSections;
    },

    extractMarkers(content) {
      if (!content) return [];

      // Extract all text within square brackets
      const markerRegex = /\[(.*?)\]/g;
      const matches = Array.from(content.matchAll(markerRegex));

      // Filter out URLs and return array of marker texts
      return matches
        .map(match => match[1].trim())
        .filter(text => {
          // Skip URLs - Check for common URL patterns
          if (text.match(/^https?:\/\//i)) return false;
          if (text.match(/^www\./i)) return false;
          if (text.match(/\.(com|org|net|io|gov|edu|co)/i)) return false;
          return true;
        });
    },

    formatMarkerTooltip(markers) {
      if (!markers || markers.length === 0) return '';

      // Format all markers for tooltip display
      return markers.map(marker => {
        // Add specific formatting based on marker type
        if (marker.toLowerCase().includes('perspective')) {
          return `🔍 ${marker}`;
        } else if (marker.toLowerCase().includes('from dynamic content')) {
          return `🔄 ${marker}`;
        } else if (marker.toLowerCase().includes('source')) {
          return `🔗 ${marker}`;
        } else {
          return `📌 ${marker}`;
        }
      }).join('\n');
    },

    updateActiveSections() {
      this.sections.forEach(section => {
        section.isActive = section.index === this.activeSegment;
      });

      // If active section is in a collapsed group, expand that group
      if (this.viewMode === 'grouped') {
        const activeSection = this.sections.find(s => s.isActive);
        if (activeSection && this.collapsedGroups[activeSection.type]) {
          this.$set(this.collapsedGroups, activeSection.type, false);
        }
      }
    },

    initializeCollapsedState() {
      // Initialize all groups as expanded except those with many items
      Object.keys(this.groupedSections).forEach(groupType => {
        if (this.groupedSections[groupType].length > 5) {
          // Collapse large groups by default
          this.$set(this.collapsedGroups, groupType, true);
        } else {
          // Keep small groups expanded
          this.$set(this.collapsedGroups, groupType, false);
        }
      });

      // Always ensure group with active segment is expanded
      const activeSection = this.sections.find(s => s.isActive);
      if (activeSection) {
        this.$set(this.collapsedGroups, activeSection.type, false);
      }
    },

    toggleGroup(groupName) {
      this.$set(this.collapsedGroups, groupName, !this.collapsedGroups[groupName]);
    },

    getGroupIcon(groupName) {
      return this.sectionTypes[groupName] ? this.sectionTypes[groupName].icon : '📄';
    },

    getGroupLabel(groupName) {
      return this.sectionTypes[groupName] ? this.sectionTypes[groupName].label : groupName;
    },

    getGroupClass(groupName) {
      return this.sectionTypes[groupName] ? this.sectionTypes[groupName].colorClass : 'default-section';
    },

    scrollToActive() {
      // Find active section in the DOM and scroll to it
      this.$nextTick(() => {
        const activeElement = this.$el.querySelector('.section-item.is-active');
        if (activeElement) {
          activeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    }
  }
}
</script>

<style scoped>
.section-navigator {
  background-color: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 0.75rem;
  margin-bottom: 1rem;
}

.theme--dark .section-navigator {
  background-color: #1a202c;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.section-title-area {
  display: flex;
  align-items: center;
}

.section-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.section-count {
  font-size: 0.75rem;
  color: #718096;
  margin-left: 0.25rem;
}

.theme--dark .section-title {
  color: #e2e8f0;
}

.section-controls {
  display: flex;
  gap: 0.25rem;
  align-items: center;
}

.view-btn {
  padding: 0.25rem 0.375rem;
  font-size: 0.875rem;
  color: #718096;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.theme--dark .view-btn {
  border-color: #2d3748;
  color: #a0aec0;
}

.view-btn:hover {
  background-color: #f7fafc;
}

.theme--dark .view-btn:hover {
  background-color: #2d3748;
}

.view-btn.active {
  color: #4299e1;
  border-color: #4299e1;
  background-color: #ebf8ff;
}

.theme--dark .view-btn.active {
  background-color: #2c5282;
}

.toggle-btn {
  padding: 0.25rem 0.375rem;
  font-size: 0.875rem;
  color: #718096;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.15s ease;
  margin-left: 0.5rem;
}

.theme--dark .toggle-btn {
  border-color: #2d3748;
  color: #a0aec0;
}

.toggle-btn:hover {
  background-color: #f7fafc;
}

.theme--dark .toggle-btn:hover {
  background-color: #2d3748;
}

.section-content {
  margin-top: 0.75rem;
  max-height: 400px;
  overflow-y: auto;
}

/* Marker badges and tooltips */
.marker-badge {
  font-size: 0.625rem;
  margin-left: 0.25rem;
  cursor: help;
}

.marker-tooltip-wrapper {
  position: relative;
  display: inline-block;
  margin-left: 0.25rem;
}

.marker-tooltip {
  visibility: hidden;
  position: absolute;
  z-index: 20;
  width: auto;
  min-width: 150px;
  max-width: 250px;
  background-color: #2d3748;
  color: #fff;
  text-align: left;
  padding: 0.375rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.6875rem;
  font-weight: normal;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  transition: opacity 0.2s;
  white-space: pre-line;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

.marker-tooltip::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: #2d3748 transparent transparent transparent;
}

.marker-tooltip-wrapper:hover .marker-tooltip {
  visibility: visible;
  opacity: 1;
}

/* Section list styles */
.section-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

/* Grouped view specific styles */
.section-group {
  margin-bottom: 0.25rem;
}

.group-header {
  display: flex;
  align-items: center;
  padding: 0.375rem;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: background-color 0.2s;
  border-left: 3px solid;
}

.group-header:hover {
  background-color: #f7fafc;
}

.theme--dark .group-header:hover {
  background-color: #2d3748;
}

.group-icon {
  font-size: 1rem;
  margin-right: 0.5rem;
}

.group-name {
  flex: 1;
  font-size: 0.8125rem;
  font-weight: 500;
}

.group-count {
  margin: 0 0.5rem;
  font-size: 0.75rem;
  color: #718096;
  background-color: #f7fafc;
  padding: 0.125rem 0.375rem;
  border-radius: 0.875rem;
}

.theme--dark .group-count {
  background-color: #2d3748;
}

.group-toggle {
  font-size: 0.625rem;
  color: #718096;
}

.group-items {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  padding: 0.25rem 0.25rem 0.25rem 1.75rem;
}

/* Mini section items within groups */
.section-item.mini {
  border-radius: 0.25rem;
  padding: 0.25rem 0.375rem;
  background-color: #f7fafc;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  border: none;
}

.theme--dark .section-item.mini {
  background-color: #2d3748;
}

.section-item.mini:hover {
  background-color: #edf2f7;
}

.theme--dark .section-item.mini:hover {
  background-color: #4a5568;
}

.section-item.mini.is-active {
  background-color: #ebf8ff;
  color: #3182ce;
}

.theme--dark .section-item.mini.is-active {
  background-color: #2c5282;
}

.segment-number {
  font-size: 0.6875rem;
  color: #718096;
}

.mini-playing-indicator {
  color: #3182ce;
  font-size: 0.625rem;
}

.segment-speaker {
  max-width: 80px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.6875rem;
}

/* Timeline view styles */
.timeline-view .section-item {
  cursor: pointer;
  border-radius: 0.25rem;
  padding: 0.375rem;
  transition: all 0.2s ease;
  border-left: 3px solid;
  margin-bottom: 0.25rem;
}

.timeline-view .section-item:hover {
  background-color: #f7fafc;
}

.theme--dark .timeline-view .section-item:hover {
  background-color: #2d3748;
}

.timeline-view .section-item.is-active {
  box-shadow: 0 0 0 1px rgba(66, 153, 225, 0.5);
}

.timeline-view .section-item-header {
  display: flex;
  align-items: center;
}

.timeline-view .section-icon {
  font-size: 1rem;
  margin-right: 0.5rem;
}

.timeline-view .section-details {
  flex: 1;
}

.timeline-view .section-label {
  font-size: 0.8125rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.timeline-view .section-speaker {
  font-size: 0.6875rem;
  color: #718096;
}

.timeline-view .playing-indicator {
  margin-left: 0.25rem;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ebf8ff;
  color: #3182ce;
  font-size: 0.6875rem;
}

.no-sections {
  text-align: center;
  padding: 1rem 0;
  color: #a0aec0;
  font-size: 0.875rem;
}

/* Scroll to active button */
.scroll-actions {
  position: sticky;
  bottom: 0;
  background-color: inherit;
  padding: 0.375rem 0;
  border-top: 1px solid #e2e8f0;
  text-align: center;
  margin-top: 0.5rem;
}

.theme--dark .scroll-actions {
  border-top-color: #2d3748;
}

.scroll-to-active-btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  background-color: #ebf8ff;
  color: #3182ce;
  border: 1px solid #bee3f8;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.scroll-to-active-btn:hover {
  background-color: #bee3f8;
}

.theme--dark .scroll-to-active-btn {
  background-color: #2c5282;
  border-color: #2b4c74;
}

.theme--dark .scroll-to-active-btn:hover {
  background-color: #2b6cb0;
}

/* Section type specific styles */
.preview-section {
  border-color: #9f7aea;
  background-color: rgba(159, 122, 234, 0.05);
}

.intro-section {
  border-color: #4299e1;
  background-color: rgba(66, 153, 225, 0.05);
}

.main-section {
  border-color: #718096;
  background-color: rgba(113, 128, 150, 0.05);
}

.break-section {
  border-color: #48bb78;
  background-color: rgba(72, 187, 120, 0.05);
}

.last-section {
  border-color: #ecc94b;
  background-color: rgba(236, 201, 75, 0.05);
}

.suspense-section {
  border-color: #f56565;
  background-color: rgba(245, 101, 101, 0.05);
}

.cliffhanger-section {
  border-color: #ed8936;
  background-color: rgba(237, 137, 54, 0.05);
}

.revelation-section {
  border-color: #667eea;
  background-color: rgba(102, 126, 234, 0.05);
}

.tension-section {
  border-color: #ed64a6;
  background-color: rgba(237, 100, 166, 0.05);
}

.analogy-section {
  border-color: #38b2ac;
  background-color: rgba(56, 178, 172, 0.05);
}

.humor-section {
  border-color: #9f7aea;
  background-color: rgba(159, 122, 234, 0.05);
}

.discussion-section {
  border-color: #4a5568;
  background-color: rgba(74, 85, 104, 0.05);
}

.insight-section {
  border-color: #805ad5;
  background-color: rgba(128, 90, 213, 0.05);
}

.resonance-section {
  border-color: #319795;
  background-color: rgba(49, 151, 149, 0.05);
}

.evolution-section {
  border-color: #38a169;
  background-color: rgba(56, 161, 105, 0.05);
}

.portal-section {
  border-color: #2b6cb0;
  background-color: rgba(43, 108, 176, 0.05);
}

.custom-section {
  border-color: #6b7280;
  background-color: rgba(107, 114, 128, 0.05);
}

/* Mobile responsive adjustments */
@media (max-width: 768px) {
  .section-navigator {
    padding: 0.5rem;
  }

  .section-content {
    max-height: 250px;
  }

  .section-header {
    padding-bottom: 0.375rem;
  }

  .group-header {
    padding: 0.25rem;
  }

  .group-items {
    padding: 0.25rem 0.25rem 0.25rem 1.25rem;
  }

  .section-icon, .group-icon {
    font-size: 0.875rem;
    margin-right: 0.375rem;
  }

  .section-title {
    font-size: 0.8125rem;
  }

  .view-btn, .toggle-btn {
    padding: 0.1875rem 0.25rem;
    font-size: 0.75rem;
  }

  .marker-tooltip {
    min-width: 120px;
    max-width: 200px;
  }
}
</style>
